'use client'

import { HeaderDropdownRecord, InternalLinkRecord } from '@/graphql/types'
import { LeftArrow, RightArrow } from '@desoutter/uikit'
import { MenuIcon, XIcon } from 'lucide-react'
import { Button, cn } from '@desoutter/uikit'
import getUrlPrefix from '@/utils/getUrlPrefix'
import { DropdownMenuContentBlock } from '@/components/Header/HeaderDropdownMenu'
import DatoLink from '@/components/DatoLink/DatoLink'
import { InternalOrExternalLinkFragment } from '@/graphql/fragments/ExternalOrInternalLink.generated'
import { LocalizedLink } from '@/components/LocalizedLink'
import { usePathname } from 'next/navigation'
import { transformLocale } from '@/utils/language'

export function HeaderMobileDropdownMenu({
	menu,
	contactPageLink,
	language,
}: {
	menu: Array<HeaderDropdownRecord>
	contactPageLink: InternalOrExternalLinkFragment
	language: string
}) {
	const pathname = usePathname()

	// Transform language from DatoCMS format to URL format
	const urlLanguage = transformLocale({
		locale: language,
		to: 'params',
	})

	return (
		<div className="header-dropdown-menu flex lg:hidden lg:w-[124px] h-[48px]">
			<input
				type="checkbox"
				id="dropdown-mobile-header-menu"
				className="peer hidden"
			/>
			<label
				aria-checked="true"
				htmlFor="dropdown-mobile-header-menu"
				className={cn(
					'min-w-[80px] p-1 clickOnPageChange relative flex items-center text-foreground peer-checked:text-background dark:peer-checked:text-foreground font-medium cursor-pointer z-10',
					'bg-gray-900/20 peer-checked:bg-white/10 dark:bg-white/10 rounded-full peer-checked:[&>.dropdown-mobile-header-menu-open]:hidden peer-checked:[&>.dropdown-mobile-header-menu-close]:flex',
				)}
			>
				<div className="select-none text-foreground font-medium ml-2 ">
					Menu
				</div>
				<div className="dropdown-mobile-header-menu-open flex ml-2 w-10 h-10  items-center justify-center rounded-full cursor-pointer z-10 bg-foreground text-background">
					<MenuIcon className="w-5 h-5" />
				</div>
				<div className="dropdown-mobile-header-menu-close hidden ml-2 w-10 h-10  items-center justify-center rounded-full cursor-pointer z-10 bg-background text-foreground">
					<XIcon className="w-5 h-5" />
				</div>
			</label>

			<div className="absolute hidden peer-checked:block top-28 left-0 w-full bg-gray-900 text-background dark:text-foreground z-30 overflow-y-scroll h-[calc(100vh-var(--height-header))]">
				{menu.map((dropdown) =>
					dropdown.simpleLink ? (
						<LocalizedLink
							locale={urlLanguage}
							key={dropdown.id}
							href={getUrlPrefix(dropdown.simpleLink)}
						>
							<label className="flex items-center justify-between mx-4 mb-6 cursor-pointer">
								<div className="mb-2 mt-0 overflow-visible text-xl font-normal">
									{dropdown.label}
								</div>
								<RightArrow variant="stroke-white" />
							</label>
						</LocalizedLink>
					) : (
						<div key={dropdown.id}>
							<label
								htmlFor={'mobileDropdown' + dropdown.id}
								className="flex items-center justify-between mx-4 mb-6 cursor-pointer"
							>
								<div className="mb-2 mt-0 overflow-visible text-xl font-normal">
									{dropdown.label}
								</div>
								<RightArrow variant="stroke-white" />
							</label>
							<input
								type="checkbox"
								id={'mobileDropdown' + dropdown.id}
								key={'mobileDropdown' + dropdown.id + pathname}
								className="peer hidden"
							/>
							<label
								htmlFor={'mobileDropdown' + dropdown.id}
								className="absolute hidden peer-checked:flex top-0 mx-4 cursor-pointer z-20 gap-3"
							>
								<LeftArrow /> Menu / {dropdown.label}
							</label>
							<div className="absolute hidden peer-checked:block top-10 z-20 w-full pb-6">
								{dropdown.blocks.map((block) => (
									<DropdownMenuContentBlock
										className={
											block.image
												? 'dark mx-4'
												: 'dark mx-4 mb-1 p-6 rounded-2xl bg-gray-800'
										}
										title={block.title}
										description={block.description}
										image={block.image?.responsiveImage}
										titleLink={block.titleLink as InternalLinkRecord}
										links={block.links as InternalLinkRecord[]}
										showIcon={block.showIcons}
										key={block.id}
									/>
								))}
								{!!dropdown.extraButtons.length && (
									<div className="flex justify-between mt-7 mx-4 gap-4">
										{dropdown.extraButtons.map((button, i) => (
											<DatoLink
												fragment={button as InternalOrExternalLinkFragment}
												key={button.label}
											>
												<Button
													color={i % 2 ? 'primary' : 'tertiary'}
													title={button.label}
													size="sm"
												>
													{button.label}
												</Button>
											</DatoLink>
										))}
									</div>
								)}
								{!!dropdown.stories.length && (
									<div className="flex justify-between my-4 mx-4 gap-4">
										{dropdown.stories.map((story, i) => (
											<DatoLink
												fragment={story as InternalOrExternalLinkFragment}
												key={story.title}
											>
												<Button
													color={i % 2 ? 'primary' : 'tertiary'}
													title={story.title}
												>
													{story.title}
												</Button>
											</DatoLink>
										))}
									</div>
								)}
							</div>
							<div className="hidden peer-checked:block absolute top-0 left-0 w-full h-full bg-gray-900" />
						</div>
					),
				)}
				<DatoLink
					fragment={contactPageLink}
					className="flex items-center justify-between mx-4 mb-6 cursor-pointer"
				>
					<div className="mb-2 mt-0 overflow-visible text-xl font-normal">
						{contactPageLink.__typename === 'InternalLinkRecord'
							? contactPageLink.label
							: 'Contact'}
					</div>
					<RightArrow variant="stroke-white" />
				</DatoLink>
			</div>
			<div className="fixed hidden peer-checked:block top-0 left-0 w-full h-full bg-gray-900" />
		</div>
	)
}
